import { create } from 'zustand';
import { BUILT_IN_PRESETS, parseStoredPresets, type Preset, type PresetSettings } from '@/core/project/preset';
import { readPreference, writePreference } from '@/lib/preferences';

interface PresetStore {
  /** The user's own presets (built-ins live in `BUILT_IN_PRESETS`). */
  readonly presets: readonly Preset[];
  save(name: string, settings: PresetSettings): Preset;
  remove(id: string): void;
}

const persist = (presets: readonly Preset[]) => writePreference('presets', JSON.stringify(presets));

/** User presets, kept on this device. */
export const usePresetStore = create<PresetStore>()((set, get) => ({
  presets: parseStoredPresets(readPreference('presets')),
  save(name, settings) {
    const preset: Preset = { id: `preset-${Date.now().toString(36)}`, name: name.trim().slice(0, 60), settings };
    const presets = [...get().presets, preset];
    persist(presets);
    set({ presets });
    return preset;
  },
  remove(id) {
    const presets = get().presets.filter((preset) => preset.id !== id);
    persist(presets);
    set({ presets });
  },
}));

/** A built-in or saved preset by id; null when there is none (a saved preset deleted since). */
export const findPresetById = (id: string): Preset | null =>
  BUILT_IN_PRESETS.find((preset) => preset.id === id) ?? usePresetStore.getState().presets.find((preset) => preset.id === id) ?? null;
